/**
 * Newcop search (sustituye a Doofinder).
 * - Al abrir: búsquedas populares, recientes y productos recomendados.
 * - Al escribir: sugerencias + colecciones (Predictive Search) y productos
 *   reordenados por ventas/stock (sections/nc-search-results.liquid).
 * - Palabra clave exacta en la tabla de redirecciones → va a la colección.
 * - Referencia / código de artículo (IG1024, DD1391-100) → si solo hay un producto, directo a él.
 * - Cada tarjeta muestra las tallas con stock; tocar una talla añade al carrito.
 * - Eventos para GTM/GA4: nc_search_open, nc_search_query, nc_search_no_results,
 *   nc_search_submit, nc_search_redirect, nc_search_click.
 */
(() => {
  if (customElements.get('nc-search')) return;

  const RECENT_KEY = 'nc_recent_searches';
  const RECENT_MAX = 6;

  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const norm = (s) =>
    String(s || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  // Analítica propia: los eventos van a una Edge Function de Supabase (tabla search_analytics.events)
  // y, al hacer clic / añadir al carrito desde el buscador, el carrito se marca con el atributo nc_search
  // para poder medir las ventas que aporta el buscador (pedido → atributos).
  const EVENTS_URL = 'https://gdjblzjvoxsxsrnmlwmu.supabase.co/functions/v1/search-events';
  const SEND_EVENTS = ['nc_search_query', 'nc_search_no_results', 'nc_search_click', 'nc_search_add_to_cart', 'nc_search_submit'];
  const sessionId = () => {
    try {
      let id = sessionStorage.getItem('nc_sid');
      if (!id) {
        id = Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem('nc_sid', id);
      }
      return id;
    } catch (e) {
      return '';
    }
  };
  const sendEvent = (event, d) => {
    const payload = JSON.stringify({
      event,
      session_id: sessionId(),
      term: d.search_term,
      results: d.search_results,
      product_id: d.product_id,
      product_title: d.product_title,
      position: d.position,
      list: d.list,
      variant_id: d.variant_id,
      size: d.size,
      locale: document.documentElement.lang,
      device: window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop',
      page: location.pathname,
    });
    if (navigator.sendBeacon && navigator.sendBeacon(EVENTS_URL, new Blob([payload], { type: 'text/plain' }))) return;
    fetch(EVENTS_URL, { method: 'POST', body: payload, keepalive: true, headers: { 'Content-Type': 'text/plain' } }).catch(() => {});
  };
  const markCart = (term) => {
    const t = String(term || '').trim();
    if (!t || t === '(vacío)') return;
    fetch('/cart/update.js', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { nc_search: t.slice(0, 60), nc_search_at: new Date().toISOString() } }),
    }).catch(() => {});
  };
  const push = (event, data) => {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event }, data));
    } catch (e) {
      /* analytics must never break search */
    }
    try {
      if (SEND_EVENTS.indexOf(event) !== -1) sendEvent(event, data || {});
      if (event === 'nc_search_click' || event === 'nc_search_add_to_cart') markCart((data || {}).search_term);
    } catch (e) {
      /* analytics must never break search */
    }
  };
  const readRecent = () => {
    try {
      const v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(v) ? v.slice(0, RECENT_MAX) : [];
    } catch (e) {
      return [];
    }
  };
  const writeRecent = (list) => {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) {
      /* storage blocked: ignore */
    }
  };
  const removeRecent = (term) => writeRecent(readRecent().filter((x) => norm(x) !== norm(term)));
  const saveRecent = (term) => {
    const t = String(term || '').trim();
    if (!t) return;
    try {
      const list = readRecent().filter((x) => norm(x) !== norm(t));
      list.unshift(t);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) {
      /* storage blocked: ignore */
    }
  };

  class NcSearch extends HTMLElement {
    connectedCallback() {
      // estilo de prueba (?nc_style=a|b|c) mientras se elige el diseño; se recuerda en la sesión
      try {
        const p = new URLSearchParams(window.location.search).get('nc_style');
        if (p) sessionStorage.setItem('nc_style', p);
        const s = sessionStorage.getItem('nc_style');
        if (s) document.documentElement.dataset.ncStyle = s;
      } catch (e) {
        /* sin sessionStorage: estilo por defecto */
      }
      const cfgEl = document.getElementById('nc-search-config');
      if (!cfgEl) return; // sin configuración: el formulario funciona como búsqueda normal
      try {
        this.cfg = JSON.parse(cfgEl.textContent);
      } catch (e) {
        return;
      }
      this.redirects = {};
      Object.keys(this.cfg.redirects || {}).forEach((k) => (this.redirects[norm(k)] = this.cfg.redirects[k]));
      this.t = this.cfg.t || {};
      // sinónimos: [alias, término] — se sustituye la palabra/frase completa antes de buscar
      this.syn = (this.cfg.synonyms || [])
        .map(([a, b]) => [norm(a), norm(b)])
        .filter(([a, b]) => a && b && a !== b)
        .sort((x, y) => y[0].length - x[0].length);
      this.cache = new Map();
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.clearBtn = this.querySelector('.nc-search__clear');
      this.emptyEl = this.querySelector('[data-nc-empty]');
      this.resultsEl = this.querySelector('[data-nc-results]');
      this.statusEl = this.querySelector('[data-nc-status]');
      this.hintEl = this.querySelector('[data-nc-hint]');
      this.details = this.closest('details');
      this.term = '';
      this.timer = null;
      this.pending = null; // búsqueda mostrada aún sin registrar (ver track / flush)
      this.peak = null;
      // al salir de la página se registra la búsqueda pendiente
      window.addEventListener('pagehide', () => this.flush());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flush();
      });

      this.input.addEventListener('input', () => this.onInput());
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.form.addEventListener('reset', () => setTimeout(() => this.onInput(), 0));
      this.addEventListener('click', (e) => this.onClick(e));
      // Móvil: al arrastrar el dedo por los resultados se cierra el teclado (como en las apps nativas).
      // Se usa touchmove (no scroll) porque al abrirse el teclado la página también hace scroll.
      const panel = this.querySelector('.nc-search__panel');
      if (panel) {
        let startY = null;
        panel.addEventListener('touchstart', (e) => { startY = e.touches[0].clientY; }, { passive: true });
        panel.addEventListener('touchmove', (e) => {
          if (startY === null || document.activeElement !== this.input) return;
          if (Math.abs(e.touches[0].clientY - startY) > 10) {
            this.input.blur();
            startY = null;
          }
        }, { passive: true });
        panel.addEventListener('touchend', () => { startY = null; }, { passive: true });
      }
      const modal = this.closest('.nc-modal');
      const dm = this.closest('details-modal');
      if (modal && dm) {
        // clic en la zona oscurecida (fuera del panel blanco) cierra el buscador
        modal.addEventListener('click', (e) => {
          if (e.target === modal && typeof dm.close === 'function') dm.close(false);
        });
      }
      if (this.details) {
        this.details.addEventListener('toggle', () => {
          if (this.details.open) this.onOpen();
          else this.flush();
        });
      }
    }

    onOpen() {
      if (!this.input.value.trim()) this.showEmpty();
      push('nc_search_open', {});
      requestAnimationFrame(() => this.input.focus());
    }

    // Referencia / código de artículo: letras + dígitos sin espacios ("IG1024", "DD1391-100", "dd1391 100")
    isCode(q) {
      const s = norm(q).replace(/[\s-]+/g, '');
      return /^(?=.*[a-z])(?=.*\d)[a-z0-9]{5,14}$/.test(s) && /^[a-z]{1,3}\d/.test(s);
    }

    rewrite(q) {
      // los códigos se buscan tal cual (guión → espacio para que Shopify encuentre "DD1391-100" escrito de cualquier forma)
      if (this.isCode(q)) return norm(q).replace(/-/g, ' ').toUpperCase();
      let s = ` ${norm(q)} `;
      this.syn.forEach(([a, b]) => {
        s = s.split(` ${a} `).join(` ${b} `);
      });
      return s.trim();
    }

    url(q) {
      return `${this.cfg.searchUrl}?q=${encodeURIComponent(this.rewrite(q))}&type=product&options%5Bprefix%5D=last`;
    }

    setHint(html) {
      if (!this.hintEl) return;
      this.hintEl.innerHTML = html || esc(this.hintEl.dataset.ncHintDefault || '');
      this.hintEl.classList.toggle('nc-search__hint--live', !!html);
    }

    chips(list, kind) {
      return list
        .map((term) => `<button type="button" class="nc-chip" data-nc-term="${esc(term)}" data-nc-kind="${kind}">${esc(term)}</button>`)
        .join('');
    }

    recommendedHtml() {
      const tpl = document.getElementById('nc-search-recommended');
      return tpl ? tpl.innerHTML : '';
    }

    recentHtml() {
      const recent = readRecent();
      if (!recent.length) return '';
      // texto plano como las búsquedas populares; la aspa aparece al pasar el ratón / siempre en táctil
      const items = recent
        .map(
          (term) =>
            `<span class="nc-recent__item"><button type="button" class="nc-recent__term" data-nc-term="${esc(term)}" data-nc-kind="recent">${esc(term)}</button><button type="button" class="nc-recent__remove" data-nc-remove="${esc(term)}" aria-label="${esc(this.t.remove || '')} ${esc(term)}"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg></button></span>`
        )
        .join('');
      return `<div class="nc-recent"><span class="nc-recent__label">${esc(this.t.recentLabel || '')}</span>${items}<button type="button" class="nc-recent__clear" data-nc-clear-recent="1">${esc(this.t.clearAll || '')}</button></div>`;
    }

    showEmpty() {
      this.setHint('');
      const popular = this.cfg.popular || [];
      let side = '';
      if (popular.length) {
        side += `<div class="nc-search__group"><p class="nc-search__heading">${esc(this.t.popularTitle || '')}</p><div class="nc-chips">${this.chips(popular, 'popular')}</div></div>`;
      }
      this.emptyEl.innerHTML = `${this.recentHtml()}<div class="nc-layout"><div class="nc-layout__side">${side}</div><div class="nc-layout__main">${this.recommendedHtml()}</div></div>`;
      this.emptyEl.hidden = false;
      this.resultsEl.hidden = true;
      this.clearBtn.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.markSizeRows(this.emptyEl);
    }

    // marca las filas de tallas que no caben enteras (desvanecido a la derecha)
    markSizeRows(root) {
      requestAnimationFrame(() => {
        root.querySelectorAll('.nc-card__sizes').forEach((row) => {
          row.classList.toggle('nc-card__sizes--more', row.scrollWidth > row.clientWidth + 2);
        });
      });
    }

    onInput() {
      const q = this.input.value.trim();
      this.clearBtn.hidden = !q;
      clearTimeout(this.timer);
      if (!q) {
        this.flush();
        this.term = '';
        if (this.controller) this.controller.abort();
        this.showEmpty();
        return;
      }
      this.timer = setTimeout(() => this.search(q), 180);
    }

    async search(q) {
      this.term = q;
      const key = norm(q);
      this.statusEl.textContent = this.t.loading || '';
      this.setAttribute('loading', '');
      try {
        let data = this.cache.get(key);
        if (!data) {
          if (this.controller) this.controller.abort();
          this.controller = new AbortController();
          const signal = this.controller.signal;
          const resultsUrl = `${this.url(q)}&section_id=nc-search-results`;
          const suggestUrl = `${this.cfg.suggestUrl}?q=${encodeURIComponent(this.rewrite(q))}&resources%5Btype%5D=query,collection&resources%5Blimit%5D=4&resources%5Boptions%5D%5Bunavailable_products%5D=last&section_id=nc-search-suggest`;
          const [r1, r2] = await Promise.all([fetch(resultsUrl, { signal }), fetch(suggestUrl, { signal })]);
          const parser = new DOMParser();
          const resDoc = parser.parseFromString(await r1.text(), 'text/html');
          const sugDoc = r2.ok ? parser.parseFromString(await r2.text(), 'text/html') : null;
          const res = resDoc.querySelector('.nc-results');
          const firstLink = res ? res.querySelector('.nc-card__link') : null;
          data = {
            count: res ? parseInt(res.dataset.ncCount || '0', 10) : 0,
            grid: res ? (res.querySelector('.nc-results__grid') || { outerHTML: '' }).outerHTML : '',
            suggest: sugDoc && sugDoc.querySelector('.nc-suggest') ? sugDoc.querySelector('.nc-suggest').innerHTML : '',
            firstUrl: firstLink ? firstLink.getAttribute('href') : '',
            firstTitle: firstLink ? firstLink.dataset.ncTitle || '' : '',
            titles: res ? Array.from(res.querySelectorAll('.nc-card__link[data-nc-title]')).slice(0, 24).map((a) => a.dataset.ncTitle || '') : [],
          };
          this.cache.set(key, data);
        }
        if (this.term !== q) return; // el usuario siguió escribiendo
        this.render(q, data);
      } catch (e) {
        if (e && e.name === 'AbortError') return;
        this.statusEl.textContent = '';
      } finally {
        if (this.term === q) this.removeAttribute('loading');
      }
    }

    // ¿Algún resultado contiene todas las palabras buscadas? Shopify devuelve resultados "parecidos"
    // (p. ej. "jordan 4" sin Air Jordan 4 en la tienda online → Jordan 1, Jordan 3...). Números = palabra exacta.
    hasExact(q, titles) {
      if (!titles || !titles.length) return true; // sin títulos no podemos juzgar: no avisar
      const match = (term) => {
        const tokens = norm(term).split(/[\s-]+/).filter(Boolean);
        if (!tokens.length) return true;
        return titles.some((title) => {
          const words = norm(title).split(/[^a-z0-9]+/).filter(Boolean);
          return tokens.every((tk) => {
            const clean = tk.replace(/[^a-z0-9]/g, '');
            if (!clean) return true;
            return /^\d+$/.test(clean) ? words.indexOf(clean) !== -1 : words.some((w) => w.indexOf(clean) === 0);
          });
        });
      };
      return match(q) || match(this.rewrite(q));
    }

    noExactText(q) {
      const lang = (document.documentElement.lang || 'es').toLowerCase().slice(0, 2);
      const m = {
        es: 'Sin coincidencias exactas para «{q}». Te mostramos resultados parecidos.',
        en: 'No exact matches for “{q}”. Showing similar results.',
        fr: 'Aucun résultat exact pour « {q} ». Voici des résultats similaires.',
        de: 'Keine genauen Treffer für „{q}“. Ähnliche Ergebnisse:',
        it: 'Nessun risultato esatto per «{q}». Ecco risultati simili.',
        pt: 'Sem resultados exatos para «{q}». Mostramos resultados parecidos.',
        pl: 'Brak dokładnych wyników dla „{q}”. Pokazujemy podobne.',
        ca: 'Cap coincidència exacta per a «{q}». Et mostrem resultats semblants.',
        nl: 'Geen exacte resultaten voor “{q}”. We tonen vergelijkbare resultaten.',
        hr: 'Nema točnih rezultata za „{q}”. Prikazujemo slične.',
      };
      return esc(m[lang] || m.es).replace('{q}', `<strong>${esc(q)}</strong>`);
    }

    render(q, data) {
      const redirect = this.redirects[norm(q)] || this.redirects[this.rewrite(q)];
      const fill = (s) => String(s || '').replace('{count}', data.count).replace('{terms}', esc(q));
      let side = data.suggest || '';
      // Clave de una colección para comparar: el handle tras /collections/, sin prefijo de idioma/mercado
      // (/en/, /fr-fr/…), sin ?_pos=… ni barra final. Así la misma colección no sale dos veces.
      const colKey = (u) => {
        let p = '';
        try {
          p = decodeURIComponent(new URL(u, window.location.origin).pathname);
        } catch (e) {
          p = String(u || '').split(/[?#]/)[0];
        }
        p = p.toLowerCase().replace(/\/+$/, '');
        const m = p.match(/\/collections\/([^/]+)/);
        return m ? m[1] : p;
      };
      if (redirect || side) {
        const box = document.createElement('div');
        box.innerHTML = side;
        let group = box.querySelector('[data-nc-group="collections"]');
        if (redirect) {
          // La colección de la palabra clave va la primera dentro de "Colecciones" (sin duplicados)
          if (!group) {
            group = document.createElement('div');
            group.className = 'nc-suggest__group';
            group.dataset.ncGroup = 'collections';
            group.innerHTML = `<p class="nc-search__heading">${esc(this.t.collections || '')}</p><ul class="nc-suggest__list" role="list"></ul>`;
          }
          box.prepend(group);
          group.querySelector('ul').insertAdjacentHTML('afterbegin', `<li><a class="nc-suggest__col nc-suggest__col--top" href="${esc(redirect.url)}" data-nc-redirect="1">${esc(redirect.title)}</a></li>`);
        }
        if (group) {
          // cada colección una sola vez (gana la primera: la de la palabra clave, si la hay)
          const seen = new Set();
          group.querySelectorAll('a').forEach((a) => {
            const k = colKey(a.getAttribute('href'));
            if (seen.has(k)) {
              const li = a.closest('li');
              (li && group.contains(li) ? li : a).remove();
            } else seen.add(k);
          });
        }
        side = box.innerHTML;
      }

      if (data.count === 0) {
        const popular = (this.cfg.popular || []).slice(0, 6);
        this.resultsEl.innerHTML = `<div class="nc-noresults"><p class="nc-noresults__title">${fill(this.t.noTitle)}</p><p class="nc-noresults__text">${esc(this.t.noText || '')}</p>${popular.length ? `<div class="nc-chips">${this.chips(popular, 'popular')}</div>` : ''}</div>${side ? `<div class="nc-noresults__side">${side}</div>` : ''}${this.recommendedHtml()}`;
        this.track(q, 0, 0);
        this.statusEl.textContent = fill(this.t.noTitle).replace(/<[^>]+>/g, '');
        this.setHint(`<span class="nc-search__hint-count">${fill(this.t.results)}</span>`);
      } else {
        const one = data.count === 1;
        const codeHit = one && this.isCode(q) && data.firstUrl; // referencia exacta → un solo producto
        const viewHref = codeHit ? data.firstUrl : redirect ? redirect.url : this.url(q);
        const viewLabel = codeHit
          ? `${esc(this.t.goToProduct || '')} · ${esc(data.firstTitle)}`
          : redirect
            ? `${esc(this.t.seeAll || '')} · ${esc(redirect.title)}`
            : fill(one ? this.t.viewAllOne : this.t.viewAll);
        const countText = fill(one ? this.t.resultOne : this.t.results);
        const fuzzy = !codeHit && !this.hasExact(q, data.titles); // también con redirección: la colección puede estar vacía
        const notice = fuzzy ? `<p class="nc-noexact" role="status" style="margin:0 0 1.2rem;padding:1rem 1.2rem;background:#f4f4f4;font-size:1.3rem;line-height:1.4;color:#000;">${this.noExactText(q)}</p>` : '';
        this.resultsEl.innerHTML = `<div class="nc-layout"><div class="nc-layout__side">${side}</div><div class="nc-layout__main">${notice}<div class="nc-recs__head"><p class="nc-search__heading">${esc(this.t.products || '')}</p><span class="nc-count">${countText}</span></div>${data.grid}<a class="nc-viewall" href="${esc(viewHref)}" data-nc-viewall="1">${viewLabel}</a></div></div>`;
        this.statusEl.textContent = countText;
        // línea bajo el campo: nº de resultados + acceso directo (colección de la palabra clave o producto de la referencia)
        const direct = codeHit
          ? `<a class="nc-search__hint-link" href="${esc(data.firstUrl)}" data-nc-viewall="1">${esc(data.firstTitle)}</a>`
          : redirect
            ? `<a class="nc-search__hint-link" href="${esc(redirect.url)}" data-nc-redirect="1">${esc(redirect.title)}</a>`
            : '';
        this.setHint(`<span class="nc-search__hint-count">${countText}</span>${direct ? ` <span class="nc-search__hint-sep" aria-hidden="true">·</span> ${direct}` : ''}`);
        this.track(q, data.count, fuzzy ? 0 : 1);
      }
      this.emptyEl.hidden = true;
      this.resultsEl.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.markSizeRows(this.resultsEl);
    }

    // Solo se registra la búsqueda final, no cada prefijo mientras se escribe ("jo", "jor", "jord"...).
    // La búsqueda mostrada queda pendiente y se registra al terminar: Enter, clic, añadir al carrito,
    // cerrar el buscador, salir de la página, vaciar el campo o empezar otra palabra distinta.
    // pending = última búsqueda mostrada; peak = la más larga de esa misma búsqueda (si el cliente
    // borra letras y luego cierra o vacía el campo, se registra "jordan", no la "j" que quedó).
    track(q, count, exact) {
      const cur = { q, count, exact };
      if (this.peak) {
        const a = norm(this.peak.q);
        const b = norm(q);
        if (b.indexOf(a) === 0) this.peak = cur; // sigue escribiendo
        else if (a.indexOf(b) !== 0) this.flush(false); // otra búsqueda: se registra la anterior
        // si borra letras (a empieza por b) se conserva la más larga
      }
      if (!this.peak) this.peak = cur;
      this.pending = cur;
    }

    // latest = true cuando el cliente actúa (Enter, clic, carrito): cuenta lo que tiene escrito ahora
    flush(latest) {
      const p = latest ? this.pending : this.peak;
      if (!this.pending || !p) return;
      this.pending = null;
      this.peak = null;
      if (p.count === 0) push('nc_search_no_results', { search_term: p.q });
      else push('nc_search_query', { search_term: p.q, search_results: p.count, search_exact: p.exact });
    }

    go(term, source) {
      const q = String(term || '').trim();
      if (!q) return;
      clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      this.flush(true);
      saveRecent(q);
      const cached = this.cache.get(norm(q));
      if (this.isCode(q) && cached && cached.count === 1 && cached.firstUrl) {
        // referencia exacta: directo al producto, sin pasar por la página de resultados
        push('nc_search_redirect', { search_term: q, search_source: source, redirect_url: cached.firstUrl });
        window.location.href = cached.firstUrl;
        return;
      }
      const redirect = this.redirects[norm(q)] || this.redirects[this.rewrite(q)];
      if (redirect) {
        push('nc_search_redirect', { search_term: q, search_source: source, redirect_url: redirect.url });
        window.location.href = redirect.url;
      } else {
        push('nc_search_submit', { search_term: q, search_source: source });
        window.location.href = this.url(q);
      }
    }

    onSubmit(e) {
      e.preventDefault();
      this.go(this.input.value, 'enter');
    }

    async addToCart(card, btn) {
      const variantId = Number(btn.dataset.ncVariant);
      if (!variantId || btn.classList.contains('is-loading')) return;
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = new FormData();
      body.append('id', String(variantId));
      body.append('quantity', '1');
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.append('sections', cart.getSectionsToRender().map((s) => s.id).join(','));
        body.append('sections_url', window.location.pathname);
      }
      btn.classList.add('is-loading');
      btn.setAttribute('aria-disabled', 'true');
      try {
        const r = await fetch(this.cfg.cartAddUrl || '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const res = await r.json();
        if (res.status) throw new Error(res.description || res.message || 'cart error');
        this.flush(true);
        push('nc_search_add_to_cart', {
          search_term: this.input.value.trim() || '(vacío)',
          product_id: card.dataset.ncProduct,
          product_title: card.dataset.ncTitle,
          variant_id: variantId,
          size: btn.textContent.trim(),
        });
        try {
          if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'nc-search', productVariantId: String(variantId) });
          }
        } catch (err) {
          /* pub/sub opcional */
        }
        // la talla se marca como añadida (✓) y se abre el carrito del tema
        btn.classList.add('is-added');
        setTimeout(() => btn.classList.remove('is-added'), 1600);
        this.statusEl.textContent = `${this.t.added || ''} ${btn.textContent.trim()} – ${card.dataset.ncTitle || ''}`;
        if (cart && typeof cart.renderContents === 'function' && res.sections) {
          const dm = this.closest('details-modal');
          if (dm && typeof dm.close === 'function') dm.close(false);
          // Igual que product-form.js del tema: con el carrito vacío (p. ej. incógnito) el cajón lleva
          // la clase is-empty, que oculta los productos aunque se hayan añadido. Hay que quitarla.
          cart.classList.remove('is-empty');
          cart.renderContents(res);
        }
      } catch (err) {
        this.statusEl.textContent = this.t.addError || '';
        btn.classList.add('is-error');
        setTimeout(() => btn.classList.remove('is-error'), 1500);
      } finally {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-disabled');
      }
    }

    onClick(e) {
      const size = e.target.closest('[data-nc-variant]');
      if (size && this.contains(size)) {
        e.preventDefault();
        this.input.blur();
        this.addToCart(size.closest('.nc-card'), size);
        return;
      }
      const rm = e.target.closest('[data-nc-remove]');
      if (rm && this.contains(rm)) {
        e.preventDefault();
        removeRecent(rm.dataset.ncRemove);
        this.showEmpty();
        return;
      }
      if (e.target.closest('[data-nc-clear-recent]')) {
        e.preventDefault();
        writeRecent([]);
        this.showEmpty();
        return;
      }
      const chip = e.target.closest('[data-nc-term]');
      if (chip && this.contains(chip)) {
        e.preventDefault();
        this.go(chip.dataset.ncTerm, chip.dataset.ncKind || 'chip');
        return;
      }
      const a = e.target.closest('a');
      if (!a || !this.contains(a)) return;
      const q = this.input.value.trim();
      if (!a.dataset.ncQuery) this.flush(true);
      if (a.dataset.ncProduct) {
        if (q) saveRecent(q);
        push('nc_search_click', {
          search_term: q || '(vacío)',
          product_id: a.dataset.ncProduct,
          product_title: a.dataset.ncTitle,
          position: Number(a.dataset.ncPos || 0),
          list: q ? 'results' : 'recommended',
        });
        return;
      }
      if (a.dataset.ncQuery) {
        e.preventDefault();
        this.go(a.dataset.ncQuery, 'suggestion');
        return;
      }
      if (a.dataset.ncRedirect || a.dataset.ncViewall || a.dataset.ncCollection) {
        if (q) saveRecent(q);
        push(a.dataset.ncViewall ? 'nc_search_submit' : 'nc_search_redirect', {
          search_term: q,
          search_source: a.dataset.ncViewall ? 'view_all' : 'collection_link',
          redirect_url: a.getAttribute('href'),
        });
      }
    }
  }

  customElements.define('nc-search', NcSearch);

  // Página de resultados (/search?q=…): clics en productos → nc_search_click (list: results_page)
  try {
    const params = new URLSearchParams(location.search);
    const term = (params.get('q') || '').trim();
    if (/\/search\/?$/.test(location.pathname) && term) {
      document.addEventListener('click', (e) => {
        const a = e.target.closest && e.target.closest('a[href*="/products/"]');
        if (!a || a.closest('nc-search') || a.closest('header')) return;
        const main = a.closest('main, #MainContent');
        if (!main) return;
        // la tarjeta (snippets/card-product.liquid) lleva el id y el título reales del producto:
        // la imagen y las tallas también son enlaces, así que el texto del enlace no sirve
        const card = a.closest('[data-product-id]');
        let pos = 0;
        if (card) {
          const ids = [];
          main.querySelectorAll('.product-card-wrapper[data-product-id]').forEach((c) => {
            const id = c.dataset.productId;
            if (id && !ids.includes(id)) ids.push(id);
          });
          pos = ids.indexOf(card.dataset.productId) + 1;
        } else {
          const links = [];
          main.querySelectorAll('a[href*="/products/"]').forEach((l) => {
            const h = l.pathname;
            if (!links.includes(h)) links.push(h);
          });
          pos = links.indexOf(a.pathname) + 1;
        }
        push('nc_search_click', {
          search_term: term,
          product_id: card ? card.dataset.productId : undefined,
          product_title: card ? card.dataset.productTitle : (a.textContent || '').trim().slice(0, 80),
          position: pos,
          list: 'results_page',
        });
      }, true);
    }
  } catch (e) {
    /* analytics must never break the page */
  }
})();
